import React, { useEffect } from 'react';
import { TDR, LANDING_PAGE_PLACEHOLDERS } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import { analytics, LINK_CLICKED_EVENT, MODAL_OPENED_EVENT } from '../../../../helpers/analytics';
import { setLang } from '../../../../helpers/setLang';
import { findBookingHorizonPolicy } from '../../../../helpers/horizon';
import { resolveLocalizedText } from '../../../../helpers/resolveLocalizedText';
import RestaurantEmailLink from '../../../buttons/RestaurantEmailLink';
import RestaurantPhoneLink from '../../../buttons/RestaurantPhoneLink';
import { ExternalLink } from '../../actions/external-link/external-link';
import { Logo } from '../../logo/logo';
import { Modal, ModalControlProps } from '../modal';

type PastHorizonProps = {
	entityWithPolicies?: TDR.EnrichedRestaurant | TDR.TableWithPolicies | null;
	restaurant: TDR.Restaurant;
} & ModalControlProps;

export const PastHorizon = ({ entityWithPolicies, restaurant, open, onOpenChange }: PastHorizonProps) => {
	const { t, i18n } = useTranslation();
	const horizonPolicy = findBookingHorizonPolicy(entityWithPolicies?.availabilityRestrictingPolicies);
	const headerText = horizonPolicy?.displays?.[0]?.title || t('checkout.modals.past_horizon.title');
	const bodyText = horizonPolicy?.displays?.[0]?.description
		|| t('checkout.modals.past_horizon.description', { days: restaurant.bookableDaysInAdvance });
	const altPlatform = restaurant.settings?.alternativePlatform?.vendor;
	const altPlatformDisplayName = TDR.RMSDisplayNameMap[altPlatform];
	const altPlatformHref = restaurant.settings?.alternativePlatform?.href;

	let href = altPlatformHref;
	if (i18n.language !== 'en') {
		href = setLang(href, i18n.language);
	}

	const logoSrc = `../../../../images/poweredBy/${altPlatform}.svg`;

	const contactByPhone = altPlatform === 'phone';
	const contactByEmail = altPlatform === 'email';
	const isCustom = altPlatform === 'custom';
	const useAltPlatform = !contactByPhone && !contactByEmail && !isCustom;

	const handleThirdPartyRMSClick = () => {
		analytics.track(LINK_CLICKED_EVENT, {
			href: href,
			purpose: 'Redirect to RMS from Horizon Past Modal',
			platform: altPlatform
		});
	};

	const LogoIcon = !altPlatformDisplayName ? null : <Logo src={logoSrc} alt={altPlatformDisplayName} />;

	const getActionText = () => {
		if (contactByPhone && !!restaurant.phoneNumber) {
			return (
				<p>
					{t('checkout.modals.past_horizon.action_phone')} <RestaurantPhoneLink phoneNumber={restaurant.phoneNumber} />.
				</p>
			);
		}

		if (contactByEmail && restaurant.contactEmail) {
			return (
				<p>
					{t('checkout.modals.past_horizon.action_email')} <RestaurantEmailLink email={restaurant?.contactEmail} />.
				</p>
			);
		}

		if (isCustom) {
			return (
				<p>
					{t('checkout.modals.past_horizon.action_platform')}
				</p>
			);
		}

		if (useAltPlatform) {
			return (
				<p>
					{t('checkout.modals.past_horizon.action_platform')}
					{!altPlatformDisplayName ? (
						'.'
					) : (
						<>
							, {t('checkout.modals.past_horizon.action_platform_with_name')} <strong>{altPlatformDisplayName}</strong>.
						</>
					)}
				</p>
			);
		}
	};

	useEffect(() => {
		if (open) {
			analytics.track(MODAL_OPENED_EVENT, { title: 'Horizon Past Modal' });
		}
	}, [open]);

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<Modal.Header closeModal={() => onOpenChange(false)}>{headerText}</Modal.Header>

			<Modal.Body>
				<p>{bodyText}</p>
				{getActionText()}
			</Modal.Body>

			<Modal.Footer>
				{isCustom ? (
					<ExternalLink
						fullWidth
						variant='secondary'
						href={href}
						aria-label={resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction}
						onClick={handleThirdPartyRMSClick}
					>
						{resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction}
					</ExternalLink>
				) : !altPlatformDisplayName || !useAltPlatform ? null : (
					<ExternalLink
						fullWidth
						variant='secondary'
						href={href}
						endIcon={LogoIcon}
						aria-label={`Find availability on ${altPlatformDisplayName}`}
						onClick={handleThirdPartyRMSClick}
					>
						{t('checkout.modals.past_horizon.find_availability')}
					</ExternalLink>
				)}
			</Modal.Footer>
		</Modal>
	);
};
